import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { SearchMd } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Avatar } from "@/components/base/avatar/avatar";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	UserTableFilters,
	type UserTableFiltersState,
} from "@/components/Molecules/FilterDrawer/UserTableFilters";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { CreatePlatformUserModal } from "@/components/Organisms/CreatePlatformUserModal/CreatePlatformUserModal";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDebounced } from "@/hooks/use-debounced";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import { useTableSettings } from "@/hooks/use-table-settings";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import { type User, useExportUsers, userQueryOptions } from "@/services/user";
import {
	buildFilterParams,
	type FilterConfig,
} from "@/utils/build-filter-params";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { getUserInitials } from "@/utils/get-user-initials";
import { formatLongDate } from "@/utils/utils";
import usersPng from "/assets/users.png";

// Avatar cell component - extracted to properly use hooks
function UserAvatarCell({ user }: { user: User }) {
	const { src: userAvatarSrc, isLoading } = useUserAvatarSrc(
		user.id,
		user.hasAvatar,
	);

	// Only show initials when not loading and no avatar
	const initials =
		!isLoading && !userAvatarSrc
			? getUserInitials(user.firstName, user.lastName)
			: undefined;

	return (
		<span className="text-sm flex items-center">
			<Avatar
				className="mr-3"
				src={userAvatarSrc || undefined}
				initials={initials}
			/>
			<div className="flex flex-col">
				{user.firstName ? `${user.firstName} ${user.lastName}` : <EmptyValue />}
				<div className="text-sm font-medium">
					{user.email ? user.email : <EmptyValue />}
				</div>
			</div>
		</span>
	);
}

// Table settings constants
const TABLE_KEY = "admin.platformUsers";
const DEFAULT_COLUMN_ORDER = [
	"nameEmail",
	"platformrole.name",
	"status.name",
	"createdat",
	"updatedat",
	"lastloggedinat",
];
const DEFAULT_SORTING = [{ id: "nameEmail", desc: false }];
const DEFAULT_PAGE_SIZE = 10;

const normalizeColumnOrder = (columnOrder?: string[]) => {
	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...DEFAULT_COLUMN_ORDER];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter((columnId) =>
		allowedColumns.has(columnId),
	);
	const unique = Array.from(new Set(sanitized));
	const missingDefaults = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults];
};

// Serializable filter state for persistence (excludes date ranges)
interface PersistedFilters {
	roles?: string[];
	status?: string[];
}
const DEFAULT_FILTERS: PersistedFilters = {
	roles: [],
	status: [],
};

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/users/(platform-users)/platform",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return z
			.object({
				mode: z.enum(["view", "edit"]).optional(),
				tab: z.enum(["details", "activity"]).optional(),
				filterOpen: z.boolean().optional(),
				create: z.enum(["platform", "organization"]).optional(),
			})
			.parse(search);
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [openPlatform, setPlatformOpen] = useState(false);
	const canCreatePlatformUser = useEntityActionAllowed({
		name: "CreateSystemUser",
	});
	const [filters, setFilters] = useState<UserTableFiltersState>({
		roles: [],
		status: [],
		createdDateRange: undefined,
		lastUpdateDateRange: undefined,
		lastLoginDateRange: undefined,
	});
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["LastName", "FirstName"],
		"OrderBy.Descending": false,
	});

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		saveFilters,
		savePageSize,
	} = useTableSettings<PersistedFilters>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: DEFAULT_FILTERS,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	// Apply persisted settings (search, filters, page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;

		if (tableSettings.searchTerm) {
			setSearchTerm(tableSettings.searchTerm);
		}
		if (tableSettings.filters) {
			setFilters((prev) => ({
				...prev,
				roles: tableSettings.filters.roles ?? [],
				status: tableSettings.filters.status ?? [],
			}));
		}
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [
		settingsLoaded,
		tableSettings.searchTerm,
		tableSettings.filters,
		tableSettings.pageSize,
	]);

	const sortFieldMapping: Record<string, string[]> = {
		nameEmail: ["LastName", "FirstName"],
		"status.name": ["Status.Name"],
		createdat: ["CreatedAt"],
		updatedat: ["UpdatedAt"],
		lastloggedinat: ["LastLoggedInAt"],
		id: ["Id"],
	};
	const startIndex = pageIndex * pageSize + 1;
	const exportMutation = useExportUsers();
	const debouncedFilters = useDebounced(filters, 500);

	const filterConfig: FilterConfig = {
		roles: { paramName: "PlatformRoleIds", transform: "joinNumbers" },
		status: { paramName: "Statuses", transform: "join" },
		"createdDateRange.start": {
			paramName: "CreatedAfter",
			transform: "dateStart",
		},
		"createdDateRange.end": {
			paramName: "CreatedBefore",
			transform: "dateEnd",
			dateStartField: "createdDateRange.start",
		},
		"lastUpdateDateRange.start": {
			paramName: "UpdatedAfter",
			transform: "dateStart",
		},
		"lastUpdateDateRange.end": {
			paramName: "UpdatedBefore",
			transform: "dateEnd",
			dateStartField: "lastUpdateDateRange.start",
		},
		"lastLoginDateRange.start": {
			paramName: "LastLoggedInAfter",
			transform: "dateStart",
		},
		"lastLoginDateRange.end": {
			paramName: "LastLoggedInBefore",
			transform: "dateEnd",
			dateStartField: "lastLoginDateRange.start",
		},
	};

	const filterParams = useMemo(
		() => buildFilterParams(debouncedFilters, filterConfig),
		[debouncedFilters],
	);

	const _userCount = useQuery(
		userQueryOptions.count({
			HasPlatformRoles: true,
			SearchTerm: searchTerm,
			...filterParams,
			...sortParams,
		}),
	);

	const unFilteredUserCount = useQuery(
		userQueryOptions.count({ HasPlatformRoles: true }),
	);

	const allUsers = useQuery(
		userQueryOptions.list(startIndex, pageSize, {
			HasPlatformRoles: true,
			SearchTerm: searchTerm,
			...filterParams,
			...sortParams,
		}),
	);

	const data = useMemo(() => {
		return allUsers.data ?? [];
	}, [allUsers.data]);

	const pageCount = useMemo(() => {
		if (!_userCount.data) return 0;
		return Math.ceil(_userCount.data / pageSize);
	}, [_userCount.data, pageSize]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await allUsers.refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleExport = (visibleColumns?: string[]) => {
		const totalCount = _userCount.data ?? 0;
		if (totalCount === 0 || totalCount > 10000) return;

		const mappedColumns = visibleColumns?.flatMap((col) => {
			if (col === "nameEmail") {
				return ["firstName", "lastName", "email"];
			}
			return col;
		});

		exportMutation.mutate(
			{
				startIndex: 1,
				count: totalCount,
				params: {
					HasPlatformRoles: true,
					SearchTerm: searchTerm,
					...filterParams,
					...sortParams,
					columns: mappedColumns?.join(","),
				},
			},
			{
				onSuccess: (blob) => {
					downloadBlob(blob, generateExportFilename("platform_users"));
				},
			},
		);
	};

	const handleRowClick = (user: User) => {
		navigate({
			to: "/admin/users/platform/$userId/details",
			params: { userId: user.id },
			search: { mode: "view" },
		});
	};

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const handleDebouncedSearchChange = (search: string) => {
		setSearchTerm(search);
		setPageIndex(0);
		// Save search term for persistence
		saveSearchTerm(search);
	};

	const handleFilterChange = (newFilters: UserTableFiltersState) => {
		setFilters(newFilters);
		// Save filters for persistence (only serializable fields)
		saveFilters({
			roles: newFilters.roles,
			status: newFilters.status,
		});
	};

	const userColumns: ColumnDef<User>[] = [
		{
			id: "nameEmail",
			accessorKey: "nameEmail",
			header: t("admin.users.tabs.table.columns.nameEmail"),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			minSize: 300,
			cell: ({ row }) => <UserAvatarCell user={row.original} />,
		},
		{
			id: "platformrole.name",
			accessorKey: "platformrole.name",
			header: t("admin.users.tabs.table.columns.role"),
			minSize: 200,
			enableSorting: false,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<>
						{user?.platformRoles && user?.platformRoles?.length > 0 ? (
							user?.platformRoles?.map((role) => {
								return (
									<p key={role.code} className="text-sm font-medium">
										{role.name}
									</p>
								);
							})
						) : (
							<EmptyValue />
						)}
					</>
				);
			},
		},
		{
			id: "status.name",
			accessorKey: "status.name",
			header: t("admin.users.tabs.table.columns.platformStatus"),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				const status = row.original.status.name as
					| "Active"
					| "Suspended"
					| "Archived"
					| "Retired";
				const colorMap: Record<
					string,
					"success" | "warning" | "gray" | "error"
				> = {
					Active: "success",
					Suspended: "warning",
					Archived: "gray",
					Retired: "error",
				};
				return row.original.status.name ? (
					<BadgeWithDot
						type="modern"
						size="md"
						color={colorMap[status] ?? "gray"}
					>
						{status}
					</BadgeWithDot>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "createdat",
			accessorKey: "createdat",
			header: t("admin.users.tabs.table.columns.created"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.createdAt ? (
					<span className="text-sm font-medium">
						{row.original.createdAt && formatLongDate(row.original.createdAt)}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "updatedat",
			accessorKey: "updatedat",
			header: t("admin.users.tabs.table.columns.lastUpdate"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.updatedAt ? (
					<span className="text-sm font-medium">
						{row.original.updatedAt && formatLongDate(row.original.updatedAt)}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "lastloggedinat",
			accessorKey: "lastloggedinat",
			header: t("admin.users.tabs.table.columns.lastLogin"),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				return row.original.lastLoggedInAt ? (
					<span className="text-sm font-medium">
						{row.original.lastLoggedInAt &&
							formatLongDate(row.original.lastLoggedInAt, {
								includeTime: true,
							})}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "id",
			accessorKey: "id",
			header: t("admin.users.tabs.table.columns.id"),
			enableSorting: false,
			minSize: 200,
			cell: ({ row }) => {
				return row.original.id ? (
					<CopyableId value={row.original.id} />
				) : (
					<EmptyValue />
				);
			},
		},
	];

	const noPlatformUsers = () => {
		if ((unFilteredUserCount.data ?? 0) > 0) {
			return (
				<div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
						<SearchMd className="size-6 text-gray-500" />
					</div>
					<div className="text-center">
						<h2 className="font-semibold text-primary text-md">
							{t("admin.users.tabs.empty.noSearchResultsPlatformTitle")}
						</h2>
						<p className="text-sm text-tertiary mt-1">
							{t("admin.users.tabs.empty.noSearchResultsDescription")}
						</p>
					</div>
				</div>
			);
		}

		return (
			<div className="flex flex-col items-center justify-center">
				<div className="relative flex items-center justify-center w-[480px] h-[480px]">
					<div className="relative flex flex-col items-center justify-center">
						<img
							src={usersPng}
							alt={t("admin.users.tabs.empty.usersAlt")}
							className=" w-[152px] h-[118px] object-contain"
						/>
						<div className="mt-4 text-center">
							<h2 className="font-semibold text-primary text-md">
								{t("admin.users.tabs.empty.platformTitle")}
							</h2>
							<p className="text-sm text-tertiary">
								{t("admin.users.tabs.empty.description")}
							</p>
						</div>
						{canCreatePlatformUser.allowed ? (
							<Button
								size="sm"
								color="primary"
								onClick={() => {
									setPlatformOpen(true);
									navigate({
										to: "/admin/users/platform",
										search: { create: "platform" },
									});
								}}
								className="mt-4"
							>
								{t("admin.users.tabs.table.actions.createPlatformUser")}
							</Button>
						) : null}
					</div>
				</div>
			</div>
		);
	};

	useEffect(() => {
		if (!pageCount) return;

		const prefetchRange = 1;

		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStartIndex = nextPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					userQueryOptions.list(nextStartIndex, pageSize, {
						HasPlatformRoles: true,
						SearchTerm: searchTerm,
						...filterParams,
						...sortParams,
					}),
				);
			}
		}

		for (let i = 1; i <= prefetchRange; i++) {
			const prevPageIndex = pageIndex - i;
			if (prevPageIndex >= 0) {
				const prevStartIndex = prevPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					userQueryOptions.list(prevStartIndex, pageSize, {
						HasPlatformRoles: true,
						SearchTerm: searchTerm,
						...filterParams,
						...sortParams,
					}),
				);
			}
		}
	}, [
		pageIndex,
		pageSize,
		pageCount,
		searchTerm,
		filterParams,
		sortParams,
		queryClient,
	]);

	if (allUsers.isError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{t("admin.users.tabs.errors.failedToLoadUsers")}
						</h3>
						<p className="text-sm text-tertiary mt-1">
							{allUsers.error?.message}
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-0">
			<CustomTableWrapper
				tableKey={TABLE_KEY}
				// Data
				data={data}
				columns={userColumns}
				// Display
				title={t("admin.users.tabs.table.title")}
				showHeaderPadding={false}
				zebraStriping
				// Loading States
				isLoading={allUsers.isLoading}
				isFetching={allUsers.isFetching}
				isRefreshing={isRefreshing}
				onRefresh={handleRefresh}
				// Pagination
				paginationMode="server"
				externalPaginationState={{ pageSize, pageIndex }}
				onPaginationChange={handlePaginationChange}
				pageCount={pageCount}
				rowCount={_userCount.data}
				totalRowCount={unFilteredUserCount.data}
				// Sorting
				sortFieldMapping={sortFieldMapping}
				defaultSortField="nameEmail"
				onSortChange={({ fields, descending }) => {
					setSortParams({
						"OrderBy.Fields": fields,
						"OrderBy.Descending": descending,
					});
				}}
				// Filtering
				filterPlaceholder={t("admin.users.platform.table.search.placeholder")}
				filterValue={searchTerm}
				onDebouncedFilterChange={handleDebouncedSearchChange}
				filtersTrailingContent={
					<UserTableFilters value={filters} onChange={handleFilterChange} />
				}
				// Table Actions
				enableTableActions
				filterLayout="bar"
				filterBarType="workspace"
				filterBarClassName="pt-0"
				hasActiveFilters={Object.keys(filterParams).length > 0}
				onExportClick={handleExport}
				isExportLoading={exportMutation.isPending}
				// Features
				enableColumnReordering
				onRowClick={handleRowClick}
				enableColumnResizing
				// Persistence - Initial values from saved settings
				initialSortingState={tableSettings.sorting}
				initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
				initialColumnVisibility={tableSettings.columnVisibility}
				settingsLoaded={settingsLoaded}
				// Persistence - Save callbacks
				onSortingStateChange={saveSorting}
				onColumnOrderChange={(order) => {
					saveColumnOrder(normalizeColumnOrder(order));
				}}
				onColumnVisibilityChange={saveColumnVisibility}
			/>

			{!allUsers.isLoading && !allUsers.isFetching && data?.length <= 0
				? noPlatformUsers()
				: null}
			<Outlet />

			{canCreatePlatformUser.allowed ? (
			<CreatePlatformUserModal
				isOpen={openPlatform}
				setIsOpen={(o) => {
					setPlatformOpen(o);
					if (!o)
						navigate({
							to: "/admin/users/platform",
						});
				}}
				handleClose={() => {
					setPlatformOpen(false);
					navigate({
						to: "/admin/users/platform",
					});
				}}
			/>
			) : null}
		</div>
	);
}
